import React, { memo, useState } from 'react';
import Separator from './Separator';
import { getOptionPathValue } from '../optionsService';
import Item from './Item';
import CustomItem from './CustomItem';
import InfoItem from './InfoItem';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mainTheme } from '@plecto/ui';

const MENU_PADDING = 4;

// One rectangle travels between rows instead of each row lighting its own fill,
// which is what the UI library's Menu does (HoverIndicator in Menu.styled.tsx).
// CSS cannot read a row's offset, so the position comes from JS and only the
// look and the slide live here.
const HoverIndicator = styled.div`
	position: absolute;
	left: ${MENU_PADDING}px;
	right: ${MENU_PADDING}px;
	top: 0;
	border-radius: 2px;
	pointer-events: none;
	background: ${mainTheme.form.dropdown.menu.hover.background};
	box-shadow: ${mainTheme.shadow.inset};
	opacity: ${props => props.$visible ? 1 : 0};
	transition: ${['transform', 'height', 'opacity']
		.map(prop => `${prop} ${mainTheme.motion.duration.instant} ${mainTheme.motion.easing.default}`)
		.join(', ')};

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const StyledMenu = styled.ul`
	// offsetTop of a row is measured against this.
	position: relative;
	max-height: 350px;
	overflow-y: auto;
	// Rows round at 2px now, so they need the panel's own inset to round against
	// rather than sitting flush to its edge.
	padding: ${MENU_PADDING}px;
	margin: 0;
	list-style: none;

	// The rectangle carries the fill. A row lighting its own as well would
	// cross-fade two at once; the label still lifts.
	.multi-level-dropdown-item {
		// Above the rectangle sliding underneath. Scoped to rows on purpose --
		// a child selector would catch the indicator too and undo its absolute
		// positioning.
		position: relative;

		&:hover {
			background-color: transparent;
			box-shadow: none;
		}
	}

	@media (max-width: 768px) {
		max-width: 100%;
		width: 100%;
	}
`;

function Menu({
	options, value, currentPath, onExpand, onSelect, onSelectIcon, fieldRelatioSelectionAllowed, nodeRef
}) {
	const { t } = useTranslation();
	const [indicator, setIndicator] = useState(null);
	const moveIndicatorTo = (row) =>
		setIndicator(row ? { top: row.offsetTop, height: row.offsetHeight } : null);

	return <StyledMenu
		className="multi-level-dropdown"
		ref={nodeRef}
		onMouseOver={e => moveIndicatorTo(
			e.target.closest('.multi-level-dropdown-item:not(.multi-level-dropdown-item-selected)')
		)}
		onMouseLeave={() => moveIndicatorTo(null)}
	>
		<HoverIndicator
			aria-hidden
			$visible={!!indicator}
			style={{
				transform: `translateY(${indicator?.top ?? 0}px)`,
				height: indicator?.height ?? 0,
			}}
		/>
		{options && options.length ? options.map(option =>
			option.separator ? <Separator key={option.value} /> : option.component ?
				<CustomItem
					key={option.value}
					className={option.className}
					onExpand={onExpand}
					selected={getOptionPathValue(currentPath, option.value) === value}
					{...option}
				/> : <Item
					data={option.data}
					key={option.value}
					className={option.className}
					onExpand={onExpand}
					onSelect={onSelect}
					onSelectIcon={onSelectIcon}
					fieldRelatioSelectionAllowed={fieldRelatioSelectionAllowed}
					selected={getOptionPathValue(currentPath, option.value) === value}
					{...option}
				/>
		) :
			<InfoItem label={t('No options')}/>}
	</StyledMenu>;
}

export default memo(Menu);
